import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { type AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';

/**
 * Whether to rewrite HTML `<details>` / `<summary>` blocks into the Rovo-native
 * expand boundary, given whatever we know about the agent that produced the
 * content.
 *
 * A known creator type decides on its own and is never overridden: third-party
 * agents (Forge, traditional 3P, remote A2A) get the rewrite, first-party agents
 * do not.
 *
 * The `rovo_chat_details_preprocess_unknown_agent` experiment governs only the
 * *unknown* case, where control keeps today's behaviour of skipping the rewrite
 * and test turns it on. Several entry paths never populate the agent — opening a
 * conversation by id from a Jira agent session leaves it absent entirely, and the
 * provisional seed carries an empty `creator_type` — so an absent value meant
 * third-party output silently rendered its raw tags on exactly those routes.
 * Rewriting anyway costs nothing when the content has no `<details>` tag, because
 * the rewrite returns its input untouched in that case.
 *
 * Shared by the live chat store and `ReadOnlyChatHistoryController` so the two
 * cannot drift apart.
 */
export const shouldPreprocessDetailsForCreatorType = (
	creatorType: AgentCreatorType | undefined,
): boolean => {
	if (creatorType) {
		return isForgeAgentByCreatorType(creatorType);
	}
	return isExperimentEnabled('rovo_chat_details_preprocess_unknown_agent');
};
